/**
 * "Since you saved" and "since you looked" prices — one rule for the browser (src/lib/saved.ts,
 * src/lib/recent.ts) and the edge (functions/api/saved.ts), so the price a visitor saved and the
 * price we show them later measure the same thing. Also the saved list's limit and its merge with
 * the backup cookie (fitSaved, mergeSaved).
 *
 * Only fees-included prices are ever compared. A price before fees is not what anyone pays, and a
 * "change" between a before-fees number and a fees-included one is the fees, not the market. The
 * build's suspect prices (shared/providers.mjs markSuspects) are always before-fees prices, so a
 * fees-included floor never contains one; the before-fees floor skips them (the edge marks them
 * first, as the build does: D1 rows keep no suspect flag).
 *
 * `drop` leaves marketplaces out: HelloTickets' recorded prices are its US storefront's and it
 * quotes visitors outside the US about a third more (src/lib/tape.ts US_STOREFRONT_ONLY), so their
 * floors are taken without it. The browser sends the same list to the API (?drop=).
 */
import { isAllIn, providerByName } from './providers.mjs';

/** @typedef {{ source?: string, from: number, allIn?: boolean, suspect?: boolean, placeholder?: boolean, stale?: boolean }} Offer */

/**
 * The lowest fees-included price among the offers (USD), or null when none includes fees.
 * @param {Offer[] | null | undefined} offers
 * @param {readonly string[] | null | undefined} [drop] marketplace names to leave out
 * @returns {number | null}
 */
export const allInFloor = (offers, drop) => {
  /** @type {number | null} */
  let best = null;
  for (const o of offers ?? []) {
    if (!o || !(typeof o.from === 'number' && o.from > 0) || (o.source && drop?.includes(o.source)) || !isAllIn(o) || o.placeholder || o.stale) continue;
    if (best === null || o.from < best) best = o.from;
  }
  return best;
};

/**
 * The lowest before-fees price, only when no offer includes fees (null otherwise): shown as
 * "$X + fees", never compared with anything.
 * @param {Offer[] | null | undefined} offers
 * @param {readonly string[] | null | undefined} [drop]
 * @returns {number | null}
 */
export const stickerFloor = (offers, drop) => {
  if (allInFloor(offers, drop) !== null) return null;
  /** @type {number | null} */
  let best = null;
  for (const o of offers ?? []) {
    if (!o || !(typeof o.from === 'number' && o.from > 0) || (o.source && drop?.includes(o.source)) || o.suspect || o.placeholder || o.stale) continue;
    if (best === null || o.from < best) best = o.from;
  }
  return best;
};

/**
 * From a saved (or last seen) fees-included price to today's: the difference in dollars, 0 under
 * 50 cents (a rounding wobble, not news), null when either side is missing.
 * @param {number | null | undefined} then
 * @param {number | null | undefined} now
 * @returns {number | null}
 */
export const priceChange = (then, now) => {
  if (!(typeof then === 'number' && then > 0 && typeof now === 'number' && now > 0)) return null;
  const d = Math.round((now - then) * 100) / 100;
  return Math.abs(d) < 0.5 ? 0 : d;
};

/**
 * The same price to the cent; two missing prices count as the same. The event page asks it of the
 * floor it shows (live-refreshed) and the floor it records (built): a "since you looked" line is
 * only true of the price on screen when the two agree.
 * @param {number | null | undefined} a
 * @param {number | null | undefined} b
 */
export const sameCents = (a, b) => (a == null || b == null ? a == null && b == null : Math.round(a * 100) === Math.round(b * 100));

/** An event id the saved-prices API answers for: "sg-18240139", "ht-2474201", a Ticketmaster
 *  "tm-…" id (the characters the /event/:id route allows). */
export const SAVED_ID = /^[a-z]{2}-[A-Za-z0-9_-]{1,60}$/;

/** Most ids one request may ask for: 30 saved and 12 recently viewed, with room to spare. */
export const SAVED_MAX_IDS = 45;

/**
 * A saved list (newest first) within its limit. Over it, the shows that are over go first (the
 * longest-saved of them first), then the longest-saved rows. The first `keep` rows, what the
 * visitor has just saved, are never dropped as a show that is over.
 * @template {{ date: string }} T
 * @param {T[]} list
 * @param {string} today the visitor's local date, YYYY-MM-DD
 * @param {number} max
 * @param {number} [keep]
 * @returns {T[]}
 */
export const fitSaved = (list, today, max, keep = 1) => {
  let next = list;
  while (next.length > max) {
    let past = -1;
    for (let i = next.length - 1; i >= keep && past < 0; i--) if (next[i].date < today) past = i;
    next = past >= 0 ? next.filter((_, i) => i !== past) : next.slice(0, max);
  }
  return next;
};

/**
 * The saved list once its backup is back (src/lib/saved.ts restoreSaved), after the browser erased
 * it and the visitor saved again meanwhile: this device's rows first, as they are (the newer save
 * of an event wins), then the backup's rows this device does not have, in the backup's order, each
 * event once; within the limit (fitSaved, this device's rows kept). A show removed on this device
 * while the backup was being read (`removed`) stays removed.
 * @template {{ id: string, date: string }} T
 * @param {T[]} mine this device's rows, newest first
 * @param {T[]} backup the backup's rows, newest first
 * @param {string} today the visitor's local date, YYYY-MM-DD
 * @param {number} max
 * @param {string[]} [removed] ids removed on this device since the merge began
 * @returns {T[]}
 */
export const mergeSaved = (mine, backup, today, max, removed = []) => {
  const have = new Set([...mine.map((s) => s.id), ...removed]);
  /** @type {T[]} */
  const more = [];
  for (const s of backup) {
    if (have.has(s.id)) continue;
    have.add(s.id);
    more.push(s);
  }
  return fitSaved([...mine, ...more], today, max, mine.length);
};

/**
 * THE BACKUP COOKIE (functions/api/saved.ts). Safari erases a site's localStorage after seven days
 * away, and a saved list is for shows weeks out. So the list also lives in a first-party cookie
 * the server sets (cookies a server sets are not erased that way): HttpOnly, Path=/api/saved, so
 * it travels with no other request. Per saved event "id.price.yyyymmdd.f", joined by "~":
 * - price: the fees-included price when saved, in cents; "s" + cents for a before-fees price when
 *   nothing included fees; empty when nothing was listed;
 * - the day it was saved;
 * - f, flags: 1 = taken without HelloTickets (a visitor outside the US); 2 = that price was the one
 *   the page showed (no live refresh had moved it), the only kind ever compared later.
 * Cookie-safe characters only, about 30 bytes an event, at most 30 events.
 */
export const SAVED_COOKIE = 'tx_saved';

/** @param {unknown} v a stored price: a positive number under $100,000, or none */
const okPrice = (v) => v == null || (typeof v === 'number' && v > 0 && v < 100000);

/**
 * @typedef {{ id: string, floorAtSave: number | null, stickerAtSave: number | null, savedAt: string, intl: boolean, seen: boolean }} SavedBackup
 */

/**
 * @param {{ id?: unknown, floorAtSave?: unknown, stickerAtSave?: unknown, savedAt?: unknown, intl?: unknown, seen?: unknown }[]} items
 * @returns {string} the cookie value ('' for none)
 */
export const encodeSavedCookie = (items) =>
  (Array.isArray(items) ? items : [])
    .filter(
      (s) =>
        s &&
        typeof s.id === 'string' &&
        SAVED_ID.test(s.id) &&
        typeof s.savedAt === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(s.savedAt) &&
        okPrice(s.floorAtSave) &&
        okPrice(s.stickerAtSave)
    )
    .slice(0, 30)
    .map((s) => {
      const price =
        typeof s.floorAtSave === 'number'
          ? String(Math.round(s.floorAtSave * 100))
          : typeof s.stickerAtSave === 'number'
            ? `s${Math.round(s.stickerAtSave * 100)}`
            : '';
      const flags = (s.intl === true ? 1 : 0) | (s.seen === true ? 2 : 0);
      return `${s.id}.${price}.${String(s.savedAt).replace(/-/g, '')}.${flags}`;
    })
    .join('~');

/**
 * @param {string | null | undefined} value
 * @returns {SavedBackup[]}
 */
export const decodeSavedCookie = (value) => {
  /** @type {SavedBackup[]} */
  const out = [];
  for (const part of String(value ?? '').split('~')) {
    const m = /^([a-z]{2}-[A-Za-z0-9_-]{1,60})\.(s?)(\d{0,7})\.(\d{4})(\d{2})(\d{2})\.([0-3])$/.exec(part);
    if (!m || (m[2] && !m[3]) || out.some((x) => x.id === m[1])) continue;
    const cents = m[3] ? Number(m[3]) / 100 : null;
    const flags = Number(m[7]);
    out.push({
      id: m[1],
      floorAtSave: m[2] ? null : cents,
      stickerAtSave: m[2] ? cents : null,
      savedAt: `${m[4]}-${m[5]}-${m[6]}`,
      intl: (flags & 1) === 1,
      seen: (flags & 2) === 2,
    });
    if (out.length >= 30) break;
  }
  return out;
};

/**
 * One cookie's value out of a Cookie header.
 * @param {string | null | undefined} header @param {string} name
 */
export const cookieValue = (header, name) => {
  for (const pair of String(header ?? '').split(';')) {
    const i = pair.indexOf('=');
    if (i > 0 && pair.slice(0, i).trim() === name) return pair.slice(i + 1).trim();
  }
  return null;
};

/**
 * The body of /api/saved's own 403 (functions/api/saved.ts): a request none of our pages made.
 * Only this answer ends a merge the browser is waiting on (src/lib/saved.ts restoreSaved). A 403
 * from anything in between, such as a Cloudflare challenge or block page, says nothing about
 * whether this browser can read its backup back.
 */
export const SAVED_REFUSAL = Object.freeze({ error: 'forbidden' });

/**
 * Is this answer /api/saved's own refusal (SAVED_REFUSAL), not a 403 from anything in between?
 * @param {number} status
 * @param {string | null | undefined} contentType
 * @param {unknown} body the answer's JSON, or null when it had none
 */
export const isSavedRefusal = (status, contentType, body) =>
  status === 403 &&
  /^application\/json\b/i.test(String(contentType ?? '')) &&
  !!body &&
  typeof body === 'object' &&
  /** @type {{ error?: unknown }} */ (body).error === SAVED_REFUSAL.error;

/**
 * The ?drop= list as the API reads it: known marketplace names only, at most three.
 * @param {string | null | undefined} raw comma-separated names
 * @returns {string[]}
 */
export const parseDrop = (raw) =>
  String(raw ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter((s, i, all) => s && all.indexOf(s) === i && providerByName(s) !== null)
    .slice(0, 3);
